import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    ChevronRight24Regular,
    ChevronDown24Regular,
} from "@fluentui/react-icons";
import SfOrgsTable from "./SfOrgsTable.jsx";
import {
    Text,
    Spinner,
    makeStyles,
    tokens,
} from "@fluentui/react-components";

import { useAdminAuth } from "../AdminAuthContext";
import { useTenantConfig } from "../useTenantConfig";
const useStyles = makeStyles({
    section: {
        width: "100%",
        margin: "2px auto",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    title: {
        margin: 0,
        fontSize: "18px",
        fontWeight: 600,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,
        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
        "& th, & td": {
            padding: "4px 4px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
            verticalAlign: "middle",
        },
    },
    expanderBtn: {
        cursor: "pointer",
        background: "transparent",
        border: "none",
        fontSize: "16px",
        display: "inline-flex",
        alignItems: "center",
        color: tokens.colorNeutralForeground1,
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
    },
    detailCell: {
        padding: 0,
        background: tokens.colorNeutralBackground3,
    },
    error: { color: tokens.colorPaletteRedForeground2, fontSize: "14px" },

    loadingPanel: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "280px", // takes space inside the section
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px dashed ${tokens.colorNeutralStroke2}`,
    },
    loadingStack: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px",
    },

    loadingTitle: {
        fontSize: "14px",
        color: tokens.colorNeutralForeground2,
    },
});

const countSfOrgs = (config) => {
    const sf = config?.sf;
    return Array.isArray(sf) ? sf.length : 0;
};

export default function TenantsTable() {
    const { userInfo } = useAdminAuth();
    const styles = useStyles();
    const { loading, error, data, setData } = useTenantConfig({
        tenantId: userInfo?.tenantId,
        enabled: !!userInfo?.tenantId,
    }
    );

    // data is raw (array or single obj) — convert only for displaying rows
    const rows = useMemo(() => {
        if (!data) return [];
        const arr = Array.isArray(data) ? data : [data];
        return arr.map((item) => ({
            // keep whole original for nested table
            ...item,
            // derived fields for top table display only
            sfCount: countSfOrgs(item.config),
        }));
    }, [data]);

    // controlled expansion + auto-expand first row
    const [expanded, setExpanded] = useState({});
    useEffect(() => {
        if (rows.length > 0 && Object.keys(expanded).length === 0) {
            const firstId = rows[0]?.tenantId ?? String(0);
            setExpanded({ [firstId]: true });
        }
    }, [rows.length]); // run once when rows appear

    const onTenantChange = async (updatedTenant) => {
        console.log('updatedTenant', updatedTenant);
        // Update local state immediately (optimistic update)
        setData((prev) => {
            if (!prev) return prev;

            const arr = Array.isArray(prev) ? prev : [prev];

            return arr.map((t) =>
                t.tenantId === updatedTenant.tenantId
                    ? updatedTenant
                    : t
            );
        });
    };

    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "expander",
                header: () => null,
                size: 40,
                cell: ({ row }) => (
                    <button
                        className={styles.expanderBtn}
                        onClick={row.getToggleExpandedHandler()}
                        aria-label={row.getIsExpanded() ? "Collapse" : "Expand"}
                        title={row.getIsExpanded() ? "Collapse" : "Expand"}
                    >
                        {row.getIsExpanded() ? (
                            <ChevronDown24Regular />
                        ) : (
                            <ChevronRight24Regular />
                        )}
                    </button>
                ),
            }),
            columnHelper.accessor("tenantId", {
                header: "Tenant ID",
                cell: (info) => <strong>{info.getValue()}</strong>,
            }),
            columnHelper.accessor("sfCount", {
                header: "Salesforce Orgs",
                cell: (info) => <span className={styles.badge}>{info.getValue()}</span>,
            }),
        ],
        [styles.badge, styles.expanderBtn],
    );

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        getRowId: (row) => row.tenantId,
        state: { expanded },
        onExpandedChange: setExpanded,
    });

    // Always expand the first row if there is at least one row and nothing expanded yet
    useEffect(() => {
        const rowModel = table.getRowModel();
        const currentExpandedIds = Object.keys(expanded).filter((k) => expanded[k]);
        if (rowModel.rows.length > 0 && currentExpandedIds.length === 0) {
            const firstRowId = rowModel.rows[0].id; // thanks to getRowId -> tenantId
            setExpanded({ [firstRowId]: true });
        }
        // We only want this to run when the row set changes OR when nothing is expanded.
    }, [rows.length]); // rows length is enough; avoids toggling user changes on each render

    return (
        <section className={styles.section}>
            <div>
                {loading && (
                    <div className={styles.loadingPanel}>
                        <div className={styles.loadingStack}>
                            <Spinner size="large" labelPosition="below" />
                            <Text weight="semibold" className={styles.loadingTitle}>
                                Fetching tenant configuration…
                            </Text>
                        </div>
                    </div>
                )}

                {error && (
                    <span className={styles.error}>
                        Failed to load. {String(error.message || error)}
                    </span>
                )}
            </div>

            {!loading && rows.length === 0 && !error && (
                <div>No tenant config found.</div>
            )}

            {rows.length > 0 && (
                <table className={styles.table}>
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id}>
                                {hg.headers.map((header) => (
                                    <th key={header.id} style={{ width: header.getSize?.() }}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Fragment key={row.id}>
                                <tr>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>

                                {row.getIsExpanded() && (
                                    <tr>
                                        <td
                                            className={styles.detailCell}
                                            colSpan={row.getVisibleCells().length}
                                        >
                                            {/* Pass the entire raw tenant. 
                         Your SfOrgsTable can now use tenant?.config?.sf directly. */}
                                            <SfOrgsTable
                                                tenant={row.original}
                                                onChange={(updatedTenant) => {
                                                    console.log("updatedTenant", updatedTenant);
                                                    // Hook up save here later if needed.
                                                    onTenantChange(updatedTenant);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}
